import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import './Sheet.css';

/** Shared two-detent mobile surface. Dragging stays on the handle so content
 * retains native scrolling, selection and touch momentum. */
export function useSheetBehavior(enabled: boolean, onDismiss: () => void, initiallyExpanded = false, collapseToDismiss = false) {
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 699px), (min-width: 700px) and (max-width: 1100px) and (orientation: portrait)').matches);
  const [viewport, setViewport] = useState(() => window.visualViewport?.height ?? window.innerHeight);
  const [expanded, setExpanded] = useState(initiallyExpanded);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- A new sheet request may promote an already mounted sheet to full height.
  useEffect(() => { if (initiallyExpanded) setExpanded(true); }, [initiallyExpanded]);
  const [entered, setEntered] = useState(false);
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const drag = useRef<{ y: number; height: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 699px), (min-width: 700px) and (max-width: 1100px) and (orientation: portrait)');
    const update = () => { setMobile(media.matches); setViewport(window.visualViewport?.height ?? window.innerHeight); };
    media.addEventListener('change', update);
    window.visualViewport?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    return () => { media.removeEventListener('change', update); window.visualViewport?.removeEventListener('resize', update); window.removeEventListener('resize', update); };
  }, []);
  const active = enabled && mobile;
  useEffect(() => {
    if (!active || entered) return;
    // Also completes when a nested preview hides the sheet mid-animation,
    // or reduced motion suppresses animationend entirely.
    const timer = window.setTimeout(() => setEntered(true), 400);
    return () => window.clearTimeout(timer);
  }, [active, entered]);
  const full = Math.max(0, viewport - 12);
  const half = viewport * .5;
  const height = dragHeight ?? (expanded ? full : half);
  const finish = (event: PointerEvent<HTMLButtonElement>, cancel = false) => {
    const start = drag.current;
    if (!start) return;
    drag.current = null;
    suppressClick.current = start.moved;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (cancel || !start.moved) { setDragHeight(null); return; }
    const next = start.height + start.y - event.clientY;
    if (next < half * .65) onDismiss();
    else if (collapseToDismiss && next <= (half + full) / 2) onDismiss();
    else { setDragHeight(null); setExpanded(next > (half + full) / 2); }
  };
  return {
    active,
    entered,
    finishEntrance: () => setEntered(true),
    expanded,
    dragging: dragHeight !== null,
    style: active ? { '--sheet-height': `${height}px`, '--sheet-viewport': `${viewport}px` } as CSSProperties : undefined,
    handleProps: {
      onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
        if (event.button !== 0) return;
        suppressClick.current = false;
        drag.current = { y: event.clientY, height, moved: false };
        event.currentTarget.setPointerCapture(event.pointerId);
      },
      onPointerMove: (event: PointerEvent<HTMLButtonElement>) => {
        const start = drag.current;
        if (!start) return;
        if (Math.abs(event.clientY - start.y) > 5) start.moved = true;
        if (start.moved) setDragHeight(Math.max(0, Math.min(full, start.height + start.y - event.clientY)));
      },
      onPointerUp: (event: PointerEvent<HTMLButtonElement>) => finish(event),
      onPointerCancel: (event: PointerEvent<HTMLButtonElement>) => finish(event, true),
      onClick: () => { if (suppressClick.current) { suppressClick.current = false; return; } if (collapseToDismiss && expanded) onDismiss(); else setExpanded(value => !value); },
      onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'ArrowUp') { event.preventDefault(); setExpanded(true); }
        if (event.key === 'ArrowDown') { event.preventDefault(); if (expanded && !collapseToDismiss) setExpanded(false); else onDismiss(); }
      },
    },
  };
}

export function SheetHandle({ behavior, disabled }: { behavior: ReturnType<typeof useSheetBehavior>; disabled: boolean }) {
  const { t } = useTranslation();
  return <button type="button" className="sheet__handle" data-testid="sheet-handle" aria-label={t(behavior.expanded ? 'sheet.collapse' : 'sheet.expand')} aria-expanded={behavior.expanded} disabled={disabled} {...behavior.handleProps}><span /></button>;
}
